// src/components/games/crossroads/MultiplayerCrossroads.tsx
import { logger } from '../../../utils/logger';
// Wrapper for Crossroads component that handles multiplayer synchronization

import React, { useState, useEffect, useCallback } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonButton,
  IonIcon,
  IonBadge,
  IonText,
  IonSpinner,
  IonAlert,
  useIonToast,
} from '@ionic/react';
import {
  arrowBack,
  cloudOfflineOutline,
  cloudDoneOutline,
  exitOutline,
} from 'ionicons/icons';
import Crossroads from './Crossroads';
import { GameState } from './types';
import {
  MultiplayerGameManager,
  ActiveMultiplayerGame,
} from '../../../services/crossroadsMultiplayerManager';

interface MultiplayerCrossroadsProps {
  gameId: string;
  playerIndex: number;
  onExit: () => void;
}

enum ConnectionState {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  ERROR = 'error',
}

export const MultiplayerCrossroads: React.FC<MultiplayerCrossroadsProps> = ({
  gameId,
  playerIndex,
  onExit,
}) => {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.CONNECTING);
  const [isMyTurn, setIsMyTurn] = useState(false);
  const [showForfeitAlert, setShowForfeitAlert] = useState(false);
  const [presentToast] = useIonToast();
  const [subscription, setSubscription] = useState<any>(null);

  // Load initial game state
  useEffect(() => {
    loadGameState();
  }, [gameId]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!gameId) return;

    setConnectionState(ConnectionState.CONNECTING);

    const channel = MultiplayerGameManager.subscribe(
      gameId,
      (updatedState) => {
        logger.log('Received game state update:', updatedState);
        setGameState(updatedState);
        checkMyTurn(updatedState);
        setConnectionState(ConnectionState.CONNECTED);
      },
      (error) => {
        logger.error('Subscription error:', error);
        setConnectionState(ConnectionState.ERROR);
        presentToast({
          message: 'Connection error. Trying to reconnect...',
          duration: 2000,
          color: 'warning',
        });
      }
    );

    setSubscription(channel);

    return () => {
      if (channel) {
        MultiplayerGameManager.unsubscribe(channel);
      }
    };
  }, [gameId]);

  const loadGameState = async () => {
    const result = await MultiplayerGameManager.joinGame(gameId);

    if (result.success && result.gameState) {
      setGameState(result.gameState);
      checkMyTurn(result.gameState);
      setConnectionState(ConnectionState.CONNECTED);
    } else {
      setConnectionState(ConnectionState.ERROR);
      presentToast({
        message: result.error || 'Failed to load game',
        duration: 2000,
        color: 'danger',
      });
    }
  };

  const checkMyTurn = (state: GameState) => {
    setIsMyTurn(state.currentPlayerIndex === playerIndex);
  };

  const handleGameStateChange = useCallback(async (newState: GameState) => {
    // Only update if it's our turn
    if (newState.currentPlayerIndex !== playerIndex) {
      presentToast({
        message: "It's not your turn!",
        duration: 1500,
        color: 'warning',
      });
      return;
    }

    // Update game state in database
    const result = await MultiplayerGameManager.updateGameState(gameId, newState);

    if (!result.success) {
      presentToast({
        message: result.error || 'Failed to update game',
        duration: 2000,
        color: 'danger',
      });

      // Reload game state on error
      await loadGameState();
    } else {
      // Local update for immediate feedback
      setGameState(newState);
      checkMyTurn(newState);
    }

    // Check for game over
    if (newState.gameOver) {
      const winnerId = newState.winner !== null
        ? newState.winner === playerIndex
          ? (await import('../../../services/supabase')).supabase.auth.getUser().then(r => r.data.user?.id)
          : 'opponent'
        : null;

      if (winnerId) {
        await MultiplayerGameManager.endGame(gameId, winnerId as string);
      }

      presentToast({
        message: newState.winner === playerIndex ? '🎉 You won!' : newState.winner === null ? 'Game Draw!' : 'You lost',
        duration: 4000,
        color: newState.winner === playerIndex ? 'success' : 'medium',
      });
    }
  }, [gameId, playerIndex]);

  const handleForfeit = async () => {
    const result = await MultiplayerGameManager.forfeitGame(gameId);

    if (result.success) {
      presentToast({
        message: 'Game forfeited',
        duration: 2000,
        color: 'medium',
      });
      onExit();
    } else {
      presentToast({
        message: result.error || 'Failed to forfeit',
        duration: 2000,
        color: 'danger',
      });
    }
  };

  const getConnectionIcon = () => {
    switch (connectionState) {
      case ConnectionState.CONNECTED:
        return cloudDoneOutline;
      case ConnectionState.CONNECTING:
        return cloudOfflineOutline;
      case ConnectionState.DISCONNECTED:
      case ConnectionState.ERROR:
        return cloudOfflineOutline;
      default:
        return cloudOfflineOutline;
    }
  };

  const getConnectionColor = () => {
    switch (connectionState) {
      case ConnectionState.CONNECTED:
        return 'success';
      case ConnectionState.CONNECTING:
        return 'warning';
      case ConnectionState.DISCONNECTED:
      case ConnectionState.ERROR:
        return 'danger';
      default:
        return 'medium';
    }
  };

  if (!gameState || connectionState === ConnectionState.CONNECTING) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={onExit}>
                <IonIcon icon={arrowBack} />
              </IonButton>
            </IonButtons>
            <IonTitle>Loading Game...</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <IonSpinner />
            <IonText color="medium">
              <p>Connecting to game...</p>
            </IonText>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <>
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={onExit}>
                <IonIcon icon={arrowBack} />
              </IonButton>
            </IonButtons>
            <IonTitle>Multiplayer Game</IonTitle>
            <IonButtons slot="end">
              {/* Connection Status */}
              <IonBadge color={getConnectionColor()} style={{ marginRight: '8px' }}>
                <IonIcon icon={getConnectionIcon()} style={{ marginRight: '4px' }} />
                {connectionState === ConnectionState.CONNECTED ? 'Live' : 'Connecting'}
              </IonBadge>

              {/* Turn Indicator */}
              {isMyTurn ? (
                <IonBadge color="success" style={{ marginRight: '8px' }}>
                  Your Turn
                </IonBadge>
              ) : (
                <IonBadge color="medium" style={{ marginRight: '8px' }}>
                  Opponent's Turn
                </IonBadge>
              )}

              {/* Forfeit Button */}
              <IonButton onClick={() => setShowForfeitAlert(true)} color="danger" fill="clear">
                <IonIcon icon={exitOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {!isMyTurn && (
            <div
              style={{
                background: 'var(--ion-color-warning)',
                color: 'var(--ion-color-warning-contrast)',
                padding: '8px',
                textAlign: 'center',
                fontWeight: 'bold',
              }}
            >
              Waiting for opponent's turn...
            </div>
          )}

          <Crossroads
            initialGameState={gameState}
            onGameStateChange={handleGameStateChange}
            disableActions={!isMyTurn || connectionState !== ConnectionState.CONNECTED}
            isMultiplayer={true}
            playerIndex={playerIndex}
          />
        </IonContent>
      </IonPage>

      {/* Forfeit Confirmation */}
      <IonAlert
        isOpen={showForfeitAlert}
        onDidDismiss={() => setShowForfeitAlert(false)}
        header="Forfeit Game?"
        message="Are you sure you want to forfeit? Your opponent will win."
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Forfeit',
            role: 'confirm',
            handler: handleForfeit,
          },
        ]}
      />
    </>
  );
};

export default MultiplayerCrossroads;
